/**
 * Handles the "Start Filing" button click in the single-order layout.
 *
 * Determines the navigation target based on whether a submission already
 * exists and how many form configs match, logs the appropriate analytics
 * events, and dispatches the modal or route change.
 */
import { NavigateFunction } from "react-router-dom";
import {
  AuthOnFile,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../enums/modalId";
import { fetchSingleMedicalPaFormConfig } from "../../../redux/slice/medicalPa/singleMedicalPaFormConfig";
import { setOpenedModalId } from "../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../redux/store/store";
import {
  logFormSelectionModalShown,
  logStartFilingClicked,
} from "./singleOrderLayoutEventLogger";

/**
 * Parameters for {@link handleStartFilingClick}.
 *
 * All order data is sourced from the Postgres-backed data-accumulator
 * context; the legacy `singleMedicalPaOrder` param has been removed.
 */
interface HandleStartFilingClickParams {
  orgId: string | null | undefined;
  userId: string | undefined;
  userEmail: string | undefined;
  payerName: string;
  submissionIsPresent: boolean;
  allMatchedFormConfigs: any[] | null | undefined;
  orderId: string | undefined;
  navigate: NavigateFunction;
  dispatch: AppDispatch;
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  coverageDetails: SingleCoverage[] | null | undefined;
  authOnFileDetails: AuthOnFile | null | undefined;
}

/**
 * Handles the start filing button click.
 * Navigates to appropriate pages or opens modals based on submission state
 * and form configuration availability.
 */
export const handleStartFilingClick = ({
  orgId,
  userId,
  userEmail,
  payerName,
  submissionIsPresent,
  allMatchedFormConfigs,
  orderId,
  navigate,
  dispatch,
  generalOrderDetails,
  coverageDetails,
  authOnFileDetails,
}: HandleStartFilingClickParams) => {
  logStartFilingClicked({
    orgId,
    userId,
    userEmail,
    payerName,
    generalOrderDetails: generalOrderDetails ?? undefined,
    coverageDetails: coverageDetails ?? undefined,
    authOnFileDetails: authOnFileDetails ?? undefined,
    order_id: generalOrderDetails?.order_id ?? "",
    patient_id: generalOrderDetails?.demographics?.patient_id ?? "",
  });

  if (submissionIsPresent) {
    navigate(`/worklists/submission-status/${orderId}`);
    return;
  }

  const primaryPayerName =
    coverageDetails?.find((c) => c.coverage_type === "primary")?.payer_name ??
    "";

  if (allMatchedFormConfigs && allMatchedFormConfigs.length > 1) {
    logFormSelectionModalShown({
      orgId,
      userId,
      userEmail,
      payerName,
      order_id: generalOrderDetails?.order_id ?? "",
      patient_id: generalOrderDetails?.demographics?.patient_id ?? "",
    });
    dispatch(
      setOpenedModalId({
        id: ModalId.FORM_SELECTION_MODAL,
        metaData: {
          formConfigs: allMatchedFormConfigs,
          payerName: primaryPayerName,
          orderId: orderId ?? "",
        },
      }),
    );
  } else {
    dispatch(
      fetchSingleMedicalPaFormConfig(allMatchedFormConfigs?.[0]?.id ?? ""),
    );
    navigate(`/worklists/medical-pa-form/${orderId ?? ""}`);
  }
};
